import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Archive, ArchiveRestore, Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  Button,
  DataTable,
  FilterBar,
  FormField,
  Modal,
  PageHeader,
  StatusBadge,
  WilayaSelect,
} from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import {
  type TiersDTO,
  type TiersInput,
  type TiersType,
  TIERS_TYPES,
  TIERS_TYPE_LABEL,
  createTiers,
  deleteTiers,
  listTiers,
  updateTiers,
} from '@/services/tiers';
import { toast } from '@/store/toastStore';

const TYPE_BADGE: Record<TiersType, 'info' | 'working' | 'review' | 'done'> = {
  CLIENT: 'info',
  FOURNISSEUR: 'working',
  SOUS_TRAITANT: 'review',
  LES_TROIS: 'done',
};

// Libellés selon le type fixé du module (Clients / Fournisseurs / Sous-traitants).
const MODULE_LABEL: Record<TiersType, { titre: string; singulier: string; sousTitre: string }> = {
  CLIENT: { titre: 'Clients', singulier: 'client', sousTitre: 'Annuaire des clients — cliquez pour voir l\'état' },
  FOURNISSEUR: { titre: 'Fournisseurs', singulier: 'fournisseur', sousTitre: 'Annuaire des fournisseurs — cliquez pour voir l\'état et les dépenses' },
  SOUS_TRAITANT: { titre: 'Sous-traitants', singulier: 'sous-traitant', sousTitre: 'Annuaire des sous-traitants — cliquez pour voir l\'état' },
  LES_TROIS: { titre: 'Tiers', singulier: 'tiers', sousTitre: 'Clients, fournisseurs et sous-traitants' },
};

export function RepertoireTiersPage({ fixedType, embedded = false }: { fixedType?: TiersType; embedded?: boolean }) {
  const perms = usePerms('tiers');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TiersType | ''>('');
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TiersDTO | null>(null);

  const effectiveType = fixedType ?? (typeFilter || undefined);
  const labels = MODULE_LABEL[fixedType ?? 'LES_TROIS'];
  // Spécialité + nombre de dépenses : pertinents pour fournisseurs / sous-traitants (pas les clients).
  const montrerSpecDep = fixedType !== 'CLIENT';

  const { data, isLoading } = useQuery({
    queryKey: ['tiers', { search, effectiveType, showArchived }],
    queryFn: () => listTiers({ q: search || undefined, type: effectiveType, archives: showArchived, size: 200 }),
  });

  const { register, handleSubmit, reset, control } = useForm<TiersInput>();

  const openCreate = () => {
    setEditing(null);
    reset({ code: '', type: fixedType ?? 'CLIENT', raison_sociale: '', is_active: true, wilaya_id: null });
    setOpen(true);
  };
  const openEdit = (t: TiersDTO) => {
    setEditing(t);
    reset(t);
    setOpen(true);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tiers'] });

  const saveMutation = useMutation({
    mutationFn: (v: TiersInput) => (editing ? updateTiers(editing.id, v) : createTiers({ ...v, type: fixedType ?? v.type })),
    onSuccess: () => { invalidate(); setOpen(false); toast.success(editing ? 'Tiers modifié.' : `${labels.singulier[0].toUpperCase()}${labels.singulier.slice(1)} créé.`); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const deleteMutation = useMutation({
    mutationFn: deleteTiers,
    onSuccess: () => { invalidate(); toast.success('Supprimé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => updateTiers(id, { is_archived: archived }),
    onSuccess: (_d, v) => { invalidate(); toast.success(v.archived ? 'Archivé.' : 'Restauré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const columns: ColumnDef<TiersDTO, unknown>[] = [
    { accessorKey: 'code', header: 'Code', size: 110 },
    {
      accessorKey: 'raison_sociale',
      header: 'Raison sociale',
      cell: ({ row }) => (
        <button onClick={() => navigate(`/tiers/${row.original.id}`)} className="font-medium text-content-primary hover:text-brand-primary hover:underline">
          {row.original.raison_sociale}
        </button>
      ),
    },
    ...(fixedType ? [] : [{
      accessorKey: 'type',
      header: 'Type',
      size: 150,
      cell: ({ getValue }: { getValue: () => unknown }) => {
        const t = getValue() as TiersType;
        return <StatusBadge status={TYPE_BADGE[t]} label={TIERS_TYPE_LABEL[t]} />;
      },
    } as ColumnDef<TiersDTO, unknown>]),
    ...(montrerSpecDep ? [{
      accessorKey: 'specialite',
      header: 'Spécialité',
      size: 150,
      cell: ({ getValue }: { getValue: () => unknown }) => {
        const s = getValue() as string | null;
        return s ? <span className="text-content-secondary">{s}</span> : <span className="text-content-muted">—</span>;
      },
    } as ColumnDef<TiersDTO, unknown>] : []),
    { accessorKey: 'rc', header: 'RC', size: 120, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'nif', header: 'NIF', cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'telephone', header: 'Téléphone', cell: ({ getValue }) => (getValue() as string) ?? '—' },
    ...(montrerSpecDep ? [{
      id: 'nb_depenses',
      header: 'Dépenses',
      size: 120,
      cell: ({ row }: { row: { original: TiersDTO } }) => {
        const n = row.original.nb_depenses;
        return n > 0 ? (
          <button
            onClick={() => navigate(`/tiers/${row.original.id}`)}
            title="Voir les dépenses liées"
            className="inline-flex items-center gap-1 rounded-full bg-brand-primary/10 px-2.5 py-0.5 text-xs font-semibold text-brand-primary hover:bg-brand-primary/20"
          >
            {n} dépense{n > 1 ? 's' : ''}
          </button>
        ) : (
          <span className="text-xs text-content-muted">—</span>
        );
      },
    } as ColumnDef<TiersDTO, unknown>] : []),
    {
      id: 'actions',
      header: '',
      size: 150,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          {showArchived ? (
            perms.canArchiver && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-done" onClick={() => archiveMutation.mutate({ id: row.original.id, archived: false })} aria-label="Restaurer">
              <ArchiveRestore className="size-4" />
            </Button>
          ) : (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/tiers/${row.original.id}`)} aria-label="Voir l'état">
                <Eye className="size-4" />
              </Button>
              {perms.canModifier && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row.original)} aria-label="Modifier">
                <Pencil className="size-4" />
              </Button>}
              {perms.canArchiver && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => archiveMutation.mutate({ id: row.original.id, archived: true })} aria-label="Archiver">
                <Archive className="size-4" />
              </Button>}
            </>
          )}
          {perms.canSupprimer && <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-status-stuck"
            onClick={() => { if (window.confirm(`Supprimer « ${row.original.raison_sociale} » ?`)) deleteMutation.mutate(row.original.id); }}
            aria-label="Supprimer"
          >
            <Trash2 className="size-4" />
          </Button>}
        </div>
      ),
    },
  ];

  return (
    <>
      {!embedded && (
        <PageHeader
          title={labels.titre}
          subtitle={labels.sousTitre}
          breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: labels.titre }]}
          actions={perms.canAjouter ? <Button onClick={openCreate}><Plus /> Nouveau {labels.singulier}</Button> : undefined}
        />
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <FilterBar search={search} onSearchChange={setSearch} searchPlaceholder="Rechercher (nom, NIF, code)…">
          {!fixedType && (
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as TiersType | '')} className="h-10 rounded-md border border-border bg-surface-secondary px-3 text-sm">
              <option value="">Tous les types</option>
              {TIERS_TYPES.map((t) => <option key={t} value={t}>{TIERS_TYPE_LABEL[t]}</option>)}
            </select>
          )}
        </FilterBar>
        <div className="flex items-center gap-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
            <input type="checkbox" className="size-4 accent-brand-primary" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Afficher les archivés
          </label>
          {embedded && <Button onClick={openCreate}><Plus /> Nouveau {labels.singulier}</Button>}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        pageSize={15}
        exportFileName={labels.titre.toLowerCase()}
        emptyMessage={showArchived ? 'Aucun élément archivé.' : `Aucun ${labels.singulier}. Créez le premier.`}
      />

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={editing ? `Modifier le ${labels.singulier}` : `Nouveau ${labels.singulier}`}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={handleSubmit((v) => saveMutation.mutate(v))} loading={saveMutation.isPending}>Enregistrer</Button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Code">
            <Input placeholder="Auto (CLI-001, FRN-001…)" {...register('code')} disabled={!!editing} />
          </FormField>
          {fixedType ? (
            <FormField label="Type">
              <Input value={TIERS_TYPE_LABEL[fixedType]} disabled />
            </FormField>
          ) : (
            <FormField label="Type">
              <select {...register('type')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
                {TIERS_TYPES.map((t) => <option key={t} value={t}>{TIERS_TYPE_LABEL[t]}</option>)}
              </select>
            </FormField>
          )}
          <FormField label="Raison sociale" required className="sm:col-span-2"><Input {...register('raison_sociale')} /></FormField>
          {montrerSpecDep && (
            <FormField label="Spécialité / domaine" hint="Ex. Électricité, Terrassement, Plomberie…" className="sm:col-span-2">
              <Input {...register('specialite')} placeholder="Ex. Électricité bâtiment" />
            </FormField>
          )}
          <FormField label="NIF"><Input {...register('nif')} /></FormField>
          <FormField label="NIS"><Input {...register('nis')} /></FormField>
          <FormField label="RC (Registre du Commerce)"><Input {...register('rc')} /></FormField>
          <FormField label="AI (Article d'Imposition)"><Input {...register('ai')} /></FormField>
          <p className="sm:col-span-2 -mt-1 text-xs text-content-muted">
            Informations fiscales (NIF, NIS, RC, Article d'imposition) — recommandées : requises pour l'état des fournisseurs (G50).
          </p>
          <FormField label="Délai livraison (jours)"><Input type="number" step="1" {...register('delai_livraison_jours')} /></FormField>
          <FormField label="Wilaya">
            <Controller control={control} name="wilaya_id" render={({ field }) => <WilayaSelect value={field.value} onChange={field.onChange} />} />
          </FormField>
          <FormField label="Adresse" className="sm:col-span-2"><Input {...register('adresse')} /></FormField>
          <FormField label="Téléphone"><Input {...register('telephone')} /></FormField>
          <FormField label="Email"><Input type="email" {...register('email')} /></FormField>
          <FormField label="Contact (nom)"><Input {...register('contact_nom')} /></FormField>
          <FormField label="Contact (téléphone)"><Input {...register('contact_telephone')} /></FormField>
          <div className="sm:col-span-2">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-content-secondary">RIB bancaire</p>
            <div className="grid grid-cols-4 gap-2">
              <Input placeholder="Banque" maxLength={3} {...register('rib_banque')} />
              <Input placeholder="Agence" maxLength={5} {...register('rib_agence')} />
              <Input placeholder="Compte" maxLength={12} {...register('rib_compte')} />
              <Input placeholder="Clé" maxLength={2} {...register('rib_cle')} />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

// Pages dédiées (routes).
export function ClientsPage() { return <RepertoireTiersPage fixedType="CLIENT" />; }
export function FournisseursPage() { return <RepertoireTiersPage fixedType="FOURNISSEUR" />; }
